/**
 * AnimatedNumber — Push #82 item C.
 *
 * Tween a numeric display from one value to another using
 * requestAnimationFrame. Used everywhere a credit balance changes — the
 * snap-update felt jank under OC's "Claude.ai-quality polish" bar, so
 * any balance number gets a smooth ~800ms tween instead.
 *
 * Used by:
 *   - SuccessModal subhead (Push #82 item B) — "N credits ready to use"
 *     animates from old → new balance
 *   - ChatCreditPill — when balance refreshes from the periodic /api/me/wallet
 *     poll, the displayed number eases instead of snapping
 *   - Profile wallet card — same pattern; balance change after grant ticks
 *   - AdminBillingAdvanced per-user allocation rows (Push #82 items D + E)
 *     — when admin grants / revokes, the affected row's balance animates
 *
 * Behavior:
 *   - First render uses `to` directly (no jump-from-zero on mount).
 *   - Subsequent `to` prop changes trigger a tween from the previous
 *     rendered value over `duration` ms.
 *   - `duration === 0` snaps without animation (escape hatch for tests
 *     or accessibility — prefers-reduced-motion is honored automatically
 *     via the reduced-motion CSS media query, fallback to direct render).
 *   - Respects `prefers-reduced-motion: reduce` — skips the tween,
 *     snaps to `to`.
 *
 * Number formatting is delegated to the caller via `format` prop. Default
 * formatter renders as locale-formatted integer.
 */

import { useEffect, useRef, useState } from 'react';

export interface AnimatedNumberProps {
  /** Target value. When this changes, the displayed value tweens to it. */
  to: number;
  /** Tween duration in ms. Default 800. Pass 0 to snap. */
  duration?: number;
  /** Format function. Default = Math.round + toLocaleString. */
  format?: (n: number) => string;
  /** Optional className for styling the wrapping <span>. */
  className?: string;
  /** Optional inline style for the wrapping <span>. */
  style?: React.CSSProperties;
}

/** Ease-out cubic — fast at start, slow at end. Feels intentional. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function defaultFormat(n: number): string {
  return Math.round(n).toLocaleString();
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function AnimatedNumber({
  to,
  duration = 800,
  format = defaultFormat,
  className,
  style,
}: AnimatedNumberProps) {
  // Track the currently-displayed value. Starts at `to` so the first
  // mount doesn't tween-from-zero.
  const [display, setDisplay] = useState<number>(to);
  // Track the previous `to` so we can detect changes + tween from
  // wherever the display currently sits (which might be mid-tween).
  const prevToRef = useRef<number>(to);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // No-op on first render — display is already `to`.
    if (prevToRef.current === to) return undefined;

    const from = display;
    const target = to;
    prevToRef.current = to;

    // Reduced-motion or duration=0 → snap.
    if (duration <= 0 || prefersReducedMotion()) {
      setDisplay(target);
      return undefined;
    }

    const startTime = performance.now();
    function step(now: number): void {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      const current = from + (target - from) * eased;
      setDisplay(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
      }
    }
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // We intentionally don't depend on `display` — that would re-trigger
    // the effect on every frame. The `from` value snapshots at the
    // moment `to` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, duration]);

  return (
    <span className={className} style={style}>
      {format(display)}
    </span>
  );
}
